<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />

    <div class="flex w-full justify-between items-center px-4 py-4 bg-dark-grey">
      <div class="text-xl px-1 font-medium">
        <span class="text-total-white">My Account</span>
      </div>

      <!-- Right Section: Save and Cancel Buttons -->
      <div class="flex items-center text-sm gap-2">
        <img
          v-if="isLoading"
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-8 h-8"
        />
      </div>
    </div>

    <!-- Edit Form -->
    <div class="flex-1 overflow-y-auto p-4" :style="{ opacity: isLoading ? 0.5 : 1 }">
      <div class="max-w-4xl mx-auto">
        <!-- Profile Image (Optional) -->
        <div
          class="flex flex-col items-center gap-2 text-prime-golden underline underline-offset-4 justify-center mb-6"
        >
          <div
            class="w-20 h-20 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark"
          >
            <template v-if="account.img">
              <div class="relative h-full w-full">
                <img
                  :src="account.img"
                  alt="Profile Image"
                  class="w-full h-full rounded-full border border-light-grey-translucent-dark z-2"
                />
                <div
                  class="absolute top-0 right-0 bg-gray-500 w-6 h-6 rounded-full p-1 z-3 flex items-center justify-center border border-light-grey-translucent-dark"
                  @click="removeImage"
                >
                  <i class="fa fa-times text-white text-sm"></i>
                </div>
              </div>
            </template>
            <template v-else>
              <i class="fa-solid fa-user text-6xl text-pitch-black -mb-[1px] mt-auto"></i>
            </template>
          </div>
          <span class="relative overflow-hidden inline-block cursor-pointer">
            <!-- Visible Upload Text -->
            <span class="text-prime-golden rounded-full"> upload image </span>
            <!-- Hidden Input Field for Image Upload -->
            <input
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              type="file"
              accept="image/*"
              @change="handleImageUpload"
            />
          </span>
        </div>

        <form class="space-y-6 text-sm">
          <!-- First Name -->
          <div>
            <label for="firstName" class="text-left block mb-1">First Name *</label>
            <input
              id="firstName"
              v-model="account.firstName"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter first name"
            />
          </div>

          <!-- Last Name -->
          <div>
            <label for="lastName" class="text-left block mb-1">Last Name *</label>
            <input
              id="lastName"
              v-model="account.lastName"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter last name"
            />
          </div>

          <!-- Company Name -->
          <div>
            <label for="company" class="text-left block mb-1">Company Name *</label>
            <input
              id="company"
              v-model="account.company"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter company name"
            />
          </div>

          <!-- Email -->
          <!--<div>
            <label for="email" class="text-left block mb-1">Email Address *</label>
            <input
              id="email"
              v-model="account.email"
              type="email"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter email address"
            />
          </div>-->

          <!-- Phone Number -->
          <!--<div>
            <PhoneInput id="phone" label="Phone number *" v-model="account.phone" />
          </div>-->

          <!-- Account Type -->
          <!--<div>
            <label for="accountType" class="text-left block mb-1">Account Type *</label>
            <input id="accountType" v-model="account.accountType" type="text" class="w-full p-3 bg-dark-grey rounded-md" placeholder="Enter account type" />
          </div>-->
        </form>

        <!-- Buttons Section: Save and Cancel -->
        <div class="flex items-center text-sm gap-2 mt-6">
          <!-- Save Button -->
          <button
            @click="saveAccount"
            class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
            :disabled="isLoading"
            :class="{ 'opacity-50': isLoading }"
          >
            Save
          </button>

          <!-- Cancel Button -->
          <button
            @click="goBack"
            class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
            :class="{ 'opacity-50': isLoading }"
            :disabled="isLoading"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Image Cropper Modal -->
    <div v-if="showCropper" class="cropper-modal">
      <cropper
        ref="cropperRef"
        class="max-h-[80dvh]"
        :src="imageToCrop"
        :aspect-ratio="1"
        :view-mode="1"
        :movable="false"
        :zoomable="false"
        :rotatable="false"
        :scalable="false"
        @cropend="onCropEnd"
      />
      <div class="flex gap-2 mt-4">
        <button
          @click="confirmCrop"
          class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
        >
          Crop Image
        </button>
        <button
          @click="showCropper = false"
          class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import TopNavbar from "@/components/TopNavbar.vue";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user"; // Import the user store
import Cropper from "vue-cropperjs"; // Import the cropper component
import "cropperjs/dist/cropper.css"; // Import cropper styles

const router = useRouter();
const userStore = useUserStore(); // Initialize the user store

const account = ref({
  id: null,
  firstName: "",
  lastName: "",
  img: "", // Default image path
  email: "",
  phone: "",
  company: "",
});

const cropperRef = ref(null); // Create a ref for the cropper
const showCropper = ref(false); // State to control cropper visibility
const imageToCrop = ref(""); // State to hold the image to crop
const isLoading = ref(false); // Updated loading state name
const croppedImageFile = ref(null); // New variable to hold the cropped image as a File

// Fetch user profile on component mount
onMounted(async () => {
  isLoading.value = true;
  await userStore.fetchUserProfile();
  const profile = userStore.profile;
  if (profile) {
    account.value = {
      id: profile.id,
      firstName: profile.first_name,
      lastName: profile.last_name,
      img: profile.profile_image_url,
      email: profile.email,
      phone: profile.phone_number,
      company: profile.company,
      accountType: profile.account_type,
    };
  }
  isLoading.value = false;
});

// Navigate back to account page
const goBack = () => {
  router.go(-1);
};

// Save account details
const saveAccount = async () => {
  // Set isLoading to true when starting to save account
  isLoading.value = true;

  try {
    // Prepare data for update
    const updatedData = {
      first_name: account.value.firstName,
      last_name: account.value.lastName,
      email: account.value.email,
      phone_number: account.value.phone.replace(/\s/g, ""),
      company: account.value.company,
      account_type: account.value.accountType,
    };

    if (croppedImageFile.value) {
      updatedData.profile_image_url = "";
    }

    await userStore.updateUserProfile(updatedData); // Update user profile

    if (croppedImageFile.value) {
      await userStore.uploadProfileImage(croppedImageFile.value);
    }

    router.go(-1);
  } catch (error) {
    console.error("Error saving account:", error);
    // Handle error (show a message to the user, etc.)
  } finally {
    isLoading.value = false; // Reset isLoading after the operation
  }
};

// Handle image upload
const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imageToCrop.value = e.target.result; // Set the image to crop
      showCropper.value = true; // Show the cropper
    };
    reader.readAsDataURL(file);
  }
};

// Confirm cropping and update the account image
const confirmCrop = () => {
  const cropper = cropperRef.value; // Access the cropper using the ref
  if (cropper) {
    const croppedCanvas = cropper.getCroppedCanvas(); // Get the cropped canvas
    account.value.img = croppedCanvas.toDataURL(); // Get cropped image for display

    // Convert the canvas to a Blob
    croppedCanvas.toBlob((blob) => {
      if (blob) {
        // Create a File from the Blob
        croppedImageFile.value = new File([blob], "cropped-image.png", {
          type: "image/png",
        });
      }
    });

    showCropper.value = false; // Hide the cropper
  }
};

// Remove image
const removeImage = () => {
  account.value.img = "";
};
</script>

<style scoped>
/* Scoped styles for form fields and buttons */
.cropper-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  height: 100dvh;
}
</style>
